import type { DomSanitizer } from '@angular/platform-browser';

import type { BarSeriesOption } from 'echarts/charts';
import type { CallbackDataParams } from 'echarts/types/dist/shared';
import { pipe } from 'rxjs';

import {
  ReportDashboardComponentChartType,
  ReportDashboardComponentKind,
  ReportDashboardComponentViewMode,
  ReportsTranslations,
  DatasetBase,
  ReportDashboardComponentViewData,
  ReportDashboardMetricOptions,
  ReportsBarChartLabelOptions,
  ReportsBarChartOptions,
  ReportsPieChartOptions,
  VisualizationMapperOperator,
  Blueprint,
} from '@core/reports-types';
import type { DurationPipe } from '@core/user-interface-legacy';
import { valueFormatter } from '@core/utils-legacy';

import { summary, table } from './generic';
import { BAR_CHART_BAR_MAX_WIDTH } from './strategies/chart-layout';
import { rateValueXAxis } from './strategies/composables/rate-axis';
import { buildSignSplitBars, pickSignSplitParam } from './strategies/composables/sign-split';
import { dataZoom } from './strategies/datazoom';
import { getXAxisNameLabel } from './strategies/get-x-axis-name-label';
import { filterByMetricKind, fromBlueprints } from './strategies/operators';
import { filterDatasetForPieChart } from './strategies/pie-dataset';
import { timeBasedTooltipContextFactory } from './strategies/time-based/tooltip';
import { xAxisMediaQueries } from './strategies/x-axis-media-queries';

// eslint-disable-next-line max-lines-per-function
const barChart = (
  component: ReportDashboardComponentViewData,
  dataset: DatasetBase,
  metric: ReportDashboardMetricOptions,
  translations: ReportsTranslations,
  durationPipe: DurationPipe,
): ReportsBarChartOptions<'value', 'category'> => {
  const dataZoomCmp = dataZoom(component, dataset.getRawData());
  const stack = component.primaryGroup || null;
  const isRateSingleSeries = metric.unitType === 'rate' && dataset.isSingleDimension;
  const labelSettings: {
    label: ReportsBarChartLabelOptions;
  } = {
    label: {
      show: dataset.isSingleDimension,
      position: 'right',
      formatter: params => {
        const data = Array.isArray(params.data) ? params.data : [params.data];
        const value = <number>data[params.seriesIndex! + 1];

        return value > 0 || metric.unitType === 'rate'
          ? valueFormatter(
              value,
              {
                unitType: dataset.metric.unitType,
                unitPrecision: dataset?.meta?.unitPrecision ?? dataset.metric.unitPrecision,
              },
              translations.i18n,
            )
          : '';
      },
      rich: {},
    },
  };

  const signSplit = isRateSingleSeries ? buildSignSplitBars(dataset, translations.i18n) : null;

  const series: BarSeriesOption[] = signSplit
    ? signSplit.series
    : dataset.dimensions.slice(1).map((dimension, index) => ({
        id: dimension.id || dimension.name,
        name: dimension.name,
        type: 'bar',
        seriesLayoutBy: 'column',
        cursor: 'default',
        ...labelSettings,
        emphasis: {
          ...labelSettings,
        },
        barMaxWidth: BAR_CHART_BAR_MAX_WIDTH,
      }));

  if (!dataset.isSingleDimension) {
    dataset.calcTotals().forEach(([categoryName, value]) => {
      series.push({
        legendHoverLink: false,
        z: 3,
        type: 'bar',
        label: {
          fontSize: 11,
          fontWeight: 600,
          position: 'right',
          show: true,
          formatter: () =>
            valueFormatter(
              value,
              {
                unitType: dataset.metric.unitType,
                unitPrecision: dataset?.meta?.unitPrecision ?? dataset.metric.unitPrecision,
              },
              translations.i18n,
            ),
          silent: true,
          rich: {},
        },
        data: [[0, categoryName]],
      });
    });
  }

  const xAxis =
    metric.unitType === 'rate'
      ? rateValueXAxis(dataset, translations.i18n)
      : {
          minInterval: 1,
          axisLabel: { show: false },
          axisTick: { show: false },
          boundaryGap: <[number, string]>[0, '5%'],
          type: 'value',
        };

  return <ReportsBarChartOptions<'value', 'category'>>{
    stack,
    dataZoom: dataZoomCmp,
    graphic: [getXAxisNameLabel(metric.xAxis)],
    ...(signSplit ? { legend: { show: false } } : {}),
    yAxis: {
      type: 'category',
      inverse: true,
      axisLabel: {
        show: true,
        formatter: (id: string, index: number) => dataset.getCategoryNameById(id),
      },
    },
    xAxis,
    tooltip: {
      show: true,
      trigger: 'axis',
      axisPointer: {
        type: 'none',
      },
      formatter: params =>
        timeBasedTooltipContextFactory(
          dataset,
          translations,
          signSplit ? <any>pickSignSplitParam(<any>params) : params,
        ),
    },
    dataset: signSplit ? signSplit.dataset : dataset.dataset,
    series,
    media: [...xAxisMediaQueries(metric)],
  };
};

const pieChart = (
  component: ReportDashboardComponentViewData,
  dataset: DatasetBase,
  metric: ReportDashboardMetricOptions,
  translations: ReportsTranslations,
  durationPipe: DurationPipe,
): ReportsPieChartOptions => ({
  dataset: dataset.dataset,
  tooltip: {
    show: true,
    trigger: 'item',
    axisPointer: {
      type: 'none',
    },
    formatter: params => timeBasedTooltipContextFactory(dataset, translations, params),
  },
  series: [
    {
      type: 'pie',
      label: {
        show: dataset.isSingleDimension,
        position: 'outside',
        formatter: (params: CallbackDataParams) => {
          const name = dataset.getCategoryNameById(params.name);
          const data = Array.isArray(params.data) ? params.data : [params.data];
          const value = valueFormatter(
            <string | number>data[params.seriesIndex! + 1],
            {
              unitType: dataset.metric.unitType,
              unitPrecision: dataset?.meta?.unitPrecision ?? dataset.metric.unitPrecision,
            },
            translations.i18n,
          );
          return `${name}\n{pct|${value}}`;
        },
      },
    },
  ],
});

export const timeBasedBlueprints = (
  sanitizer: DomSanitizer,
  translations: ReportsTranslations,
  durationPipe: DurationPipe,
): Array<Blueprint<any>> => [
  table(translations.i18n, ReportDashboardComponentKind.TimeBased),
  summary(translations.i18n, ReportDashboardComponentKind.TimeBased),
  {
    match: {
      kind: ReportDashboardComponentKind.TimeBased,
      viewMode: ReportDashboardComponentViewMode.Chart,
      chartType: ReportDashboardComponentChartType.Bar,
    },
    factory: ({ component, dataset: originalDataset, metric, meta }) => {
      let dataset = originalDataset;
      if (meta.truncateDataset) {
        const truncateLength =
          typeof meta.truncateDataset === 'number'
            ? meta.truncateDataset
            : meta.truncateDataset(component, dataset);
        dataset = dataset.truncateDataset('first', truncateLength);
      }
      return {
        vis: barChart(component, dataset, metric, translations, durationPipe),
        dataset,
      };
    },
  },
  {
    match: {
      kind: ReportDashboardComponentKind.TimeBased,
      viewMode: ReportDashboardComponentViewMode.Chart,
      chartType: ReportDashboardComponentChartType.Pie,
    },
    factory: ({ component, dataset, metric }) => {
      const filteredDataset = filterDatasetForPieChart(dataset);
      return {
        vis: pieChart(component, filteredDataset, metric, translations, durationPipe),
        dataset: filteredDataset,
      };
    },
  },
];

export const timeBased = (
  sanitizer: DomSanitizer,
  translations: ReportsTranslations,
  durationPipe: DurationPipe,
): VisualizationMapperOperator =>
  pipe(
    filterByMetricKind(ReportDashboardComponentKind.TimeBased),
    fromBlueprints(timeBasedBlueprints(sanitizer, translations, durationPipe)),
  );
